import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const label = (ym) => `${MONTHS[Number(ym.slice(5)) - 1]} ${ym.slice(2, 4)}`;
const eur = (n) => `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

function Kpi({ title, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{title}</p>
      <p className="font-serif text-3xl text-olive mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export function MonthlyStats() {
  const [months, setMonths] = useState("12");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/stats/monthly", { params: { months: Number(months) } }).then(({ data }) => setData(data));
  }, [months]);

  if (!data) return null;
  const rows = data.months.map((m) => ({ ...m, name: label(m.month) }));
  const cur = rows[rows.length - 1] || {};
  const tot = rows.reduce((a, m) => ({ revenue: a.revenue + m.revenue, orders_revenue: a.orders_revenue + m.orders_revenue, nights: a.nights + m.nights, bookings: a.bookings + m.bookings }), { revenue: 0, orders_revenue: 0, nights: 0, bookings: 0 });
  const avgOcc = rows.length ? (rows.reduce((a, m) => a + m.occupancy, 0) / rows.length).toFixed(0) : 0;

  return (
    <div className="space-y-6" data-testid="monthly-stats">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-serif text-2xl text-olive">Statistiques mensuelles <span className="text-sm text-muted-foreground font-sans">· {data.listings} logement(s)</span></h3>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="w-36 bg-white" data-testid="stats-range-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="6">6 mois</SelectItem><SelectItem value="12">12 mois</SelectItem><SelectItem value="24">24 mois</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi title="Revenus séjours" value={eur(tot.revenue)} sub={`ce mois : ${eur(cur.revenue || 0)}`} testid="kpi-revenue" />
        <Kpi title="Taux d'occupation" value={`${avgOcc} %`} sub={`ce mois : ${cur.occupancy ?? 0} % · ${tot.nights} nuits`} testid="kpi-occupancy" />
        <Kpi title="Réservations" value={tot.bookings} sub={`ce mois : ${cur.bookings ?? 0}`} testid="kpi-bookings" />
        <Kpi title="Commandes drive" value={eur(tot.orders_revenue)} sub={`ce mois : ${cur.orders ?? 0} commande(s)`} testid="kpi-orders" />
      </div>
      <div className="rounded-2xl bg-white border border-border p-4">
        <p className="text-sm font-semibold text-olive mb-2">Revenus séjours & drive (€) et taux d'occupation (%)</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="eur" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(v, n) => (n === "Occupation" ? `${v} %` : eur(v))} />
              <Legend />
              <Bar yAxisId="eur" dataKey="revenue" name="Séjours" fill="#5C6B3E" radius={[6, 6, 0, 0]} />
              <Bar yAxisId="eur" dataKey="orders_revenue" name="Drive" fill="#C85272" radius={[6, 6, 0, 0]} />
              <Line yAxisId="pct" type="monotone" dataKey="occupancy" name="Occupation" stroke="#D883A8" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="stats-table">
          <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">Mois</th><th className="p-3">Revenus</th><th className="p-3">Résa.</th><th className="p-3">Nuits</th><th className="p-3">Occupation</th><th className="p-3">Drive</th></tr>
          </thead>
          <tbody>
            {[...rows].reverse().map((m) => (
              <tr key={m.month} className="border-t border-border" data-testid={`stats-row-${m.month}`}>
                <td className="p-3 font-semibold text-olive">{m.name}</td><td className="p-3">{eur(m.revenue)}</td>
                <td className="p-3">{m.bookings}{m.site_bookings ? <span className="text-xs text-muted-foreground"> ({m.site_bookings} site)</span> : ""}</td>
                <td className="p-3">{m.nights}</td><td className="p-3">{m.occupancy} %</td>
                <td className="p-3">{m.orders} · {eur(m.orders_revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
